/**
 * IntegrationIcon (2026-08-20)
 *
 * ONE app-icon element for every surface that names an integration —
 * onboarding rows, the full-catalog modal, the post-connect scope sheet, and
 * the Integrations page tiles. Renders the toolkit's real logo (the same
 * Composio `meta.logo` the Integrations page uses) and falls back to the gold
 * letter monogram only while the logo is unknown or its URL fails to load.
 *
 * Why a component instead of inline JSX: the 2026-08-20 owner report showed
 * three hand-rolled copies of img-or-letter drifting apart — and every copy's
 * onError set `display:none`, leaving a BLANK gap (not even a letter) on a
 * 404ing logo. Centralizing makes the fallback stateful and correct once.
 */

import React, { useState, useEffect } from 'react';

export interface IntegrationIconProps {
  /** Logo URL when known (toolkit `meta.logo`); letter fallback otherwise. */
  logo?: string;
  /** Display name — first character becomes the fallback monogram. */
  name: string;
  /** Square edge in px. Border radius scales with it. */
  size: number;
  style?: React.CSSProperties;
}

export const IntegrationIcon: React.FC<IntegrationIconProps> = ({ logo, name, size, style }) => {
  const [failed, setFailed] = useState(false);
  // A logo that arrives late (catalog fetch landing after first paint) or
  // changes must clear a stale failure from a previous URL.
  useEffect(() => { setFailed(false); }, [logo]);

  const radius = Math.max(4, Math.round(size / 4));
  if (logo && !failed) {
    return (
      <img
        src={logo}
        alt=""
        aria-hidden="true"
        style={{ width: size, height: size, borderRadius: radius, objectFit: 'contain', flexShrink: 0, ...style }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: radius, flexShrink: 0,
        background: 'rgba(198, 167, 94, 0.16)', color: '#7a5f1f',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: `${Math.max(0.6, (size * 0.032)).toFixed(2)}rem`, fontWeight: 700, textTransform: 'uppercase',
        ...style,
      }}
    >
      {(name || '?').charAt(0)}
    </span>
  );
};

export default IntegrationIcon;
